import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Play, Pause, SkipBack, SkipForward, Calendar, TrendingUp, AlertTriangle, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TimeAnimationControls({ 
  onTimelineChange, 
  isPlaying, 
  setIsPlaying,
  currentMonth,
  setCurrentMonth 
}) {
  const [playbackSpeed, setPlaybackSpeed] = useState(1500); // ms per month
  
  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['geo-timeline'],
    queryFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.get(`${BACKEND_URL}/api/geo/contracts/timeline?months_ahead=12`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
  });

  const months = timelineData?.timeline ? Object.keys(timelineData.timeline).sort() : [];
  const currentData = timelineData?.timeline?.[months[currentMonth]];

  // Auto-play effect
  useEffect(() => {
    if (!isPlaying || months.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentMonth(prev => {
        const next = prev + 1;
        if (next >= months.length) {
          setIsPlaying(false);
          return 0;
        }
        return next;
      });
    }, playbackSpeed);

    return () => clearInterval(timer);
  }, [isPlaying, months.length, playbackSpeed, setCurrentMonth, setIsPlaying]);

  // Notify parent of timeline changes
  useEffect(() => {
    if (currentData && onTimelineChange) {
      onTimelineChange(currentData.featureCollection, currentData.label);
    }
  }, [currentMonth, currentData, onTimelineChange]);

  const handleSliderChange = useCallback((value) => {
    setCurrentMonth(value[0]);
    setIsPlaying(false);
  }, [setCurrentMonth, setIsPlaying]);

  const skipToStart = () => {
    setCurrentMonth(0);
    setIsPlaying(false);
  };

  const skipToEnd = () => {
    setCurrentMonth(months.length - 1);
    setIsPlaying(false);
  };

  if (isLoading || months.length === 0) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 text-xs text-muted-foreground">
        Loading timeline...
      </div>
    );
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-4 space-y-3" data-testid="time-animation-controls">
      {/* Current Month Display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#09409C]" />
          <span className="font-semibold text-sm">{currentData?.label || 'Loading...'}</span>
        </div>
        <Badge variant="outline" className="text-xs">
          {currentMonth + 1} / {months.length}
        </Badge>
      </div>

      {/* Stats for Current Month */}
      {currentData?.stats && (
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="flex flex-col items-center p-2 bg-gray-50 rounded">
            <span className="text-muted-foreground">Contracts</span>
            <span className="font-bold text-[#09409C]">{currentData.stats.totalContracts}</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-gray-50 rounded">
            <span className="text-muted-foreground">Value</span>
            <span className="font-bold text-[#229644]">{formatCurrency(currentData.stats.totalValue)}</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-gray-50 rounded">
            <span className="text-muted-foreground">At Risk</span>
            <span className="font-bold text-red-600">{currentData.stats.atRiskCount}</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-gray-50 rounded">
            <span className="text-muted-foreground">Expiring</span>
            <span className="font-bold text-orange-600">{currentData.stats.expiringThisMonth}</span>
          </div>
        </div>
      )}

      {/* Timeline Slider */}
      <div className="px-2">
        <Slider
          value={[currentMonth]}
          onValueChange={handleSliderChange}
          max={months.length - 1}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>{timelineData?.timeline?.[months[0]]?.label}</span>
          <span>{timelineData?.timeline?.[months[months.length - 1]]?.label}</span>
        </div>
      </div>

      {/* Playback Controls */}
      <div className="flex items-center justify-center gap-2">
        <Button 
          variant="outline" 
          size="icon" 
          className="h-8 w-8"
          onClick={skipToStart}
        >
          <SkipBack className="h-4 w-4" />
        </Button>
        <Button 
          variant={isPlaying ? "destructive" : "default"}
          size="icon" 
          className="h-10 w-10"
          onClick={() => setIsPlaying(!isPlaying)}
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="h-8 w-8"
          onClick={skipToEnd}
        >
          <SkipForward className="h-4 w-4" />
        </Button>
      </div>

      {/* Speed Control */}
      <div className="flex items-center justify-center gap-2 text-xs">
        <span className="text-muted-foreground">Speed:</span>
        <Button 
          variant={playbackSpeed === 2500 ? "default" : "ghost"} 
          size="sm" 
          className="h-6 px-2 text-xs"
          onClick={() => setPlaybackSpeed(2500)}
        >
          0.5x
        </Button>
        <Button 
          variant={playbackSpeed === 1500 ? "default" : "ghost"} 
          size="sm" 
          className="h-6 px-2 text-xs"
          onClick={() => setPlaybackSpeed(1500)}
        >
          1x
        </Button>
        <Button 
          variant={playbackSpeed === 750 ? "default" : "ghost"} 
          size="sm" 
          className="h-6 px-2 text-xs"
          onClick={() => setPlaybackSpeed(750)}
        >
          2x
        </Button>
      </div>
    </div>
  );
}
